import {objectFromEntries, groupBy} from '@benchling/js-utils';
import {Group} from '@visx/group';
import {scaleOrdinal} from '@visx/scale';
import produce from 'immer';
import React, {useState} from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {DataFrame, DataFrameRow, DataType} from 'data-frames/DataFrameTypes';
import {
  ColumnsMapping,
  ColumnValidationMap,
  numericAccessor,
  resolvedStringAccessor,
} from 'data-frames/util/AccessorUtils';
import {createAccessors, DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType, NUMERICAL_LEAF_TYPES} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_RESOLUTION_CONFIG} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {assertNever} from 'util/Types';

import {AxisBottom, AxisLeft, AxisOrientation} from './internals/Axis';
import {
  BAR_CHART_TYPE_TO_CONTENT_COMPONENT,
  BAR_PADDING_BETWEEN_GROUPS,
} from './internals/bar-chart/BarChartConstants';
import {
  BarChartAccessors,
  BarChartHoverInfo,
  BarChartScales,
  BarChartType,
  BarGroupDicts,
  BarGroupRowMaps,
} from './internals/bar-chart/BarChartTypes';
import {getGroupedBarChartYExtent} from './internals/bar-chart/GroupedBarChartContent';
import {getStackedBarChartYExtent} from './internals/bar-chart/StackedBarChartContent';
import BenchlingChart from './internals/BenchlingChart';
import ChartMask, {getChartMaskId} from './internals/cartesian-plots/ChartMask';
import ChartGrid from './internals/ChartGrid';
import ChartTooltip, {
  estimateTooltipHeight,
  TooltipInfo,
  useChartTooltip,
  UseChartTooltipInput,
} from './internals/ChartTooltip';
import DiscreteSeriesLegend, {estimateDiscreteSeriesLegendHeight} from './internals/DiscreteSeriesLegend';
import {useSeriesExcludedWithDataFrameState} from './internals/hooks/useSeriesExcludedWithDataFrameState';
import fitChartAndLabels from './internals/utils/fitChartAndLabels';
import getChartDimensions from './internals/utils/getChartDimensions';
import getDiscreteAxisNumTicks from './internals/utils/getDiscreteAxisNumTicks';
import {DiscreteSeriesColorConfig} from './internals/utils/getSeriesColorConfig';
import getTickLabelWidths from './internals/utils/getTickLabelWidths';
import getVisxAxisConfig, {
  assertVisxAxisConfigScaleType,
  BandVisxAxisConfig,
  isVisxAxisConfigScaleType,
  LinearVisxAxisConfig,
  LogVisxAxisConfig,
} from './internals/utils/getVisxAxisConfig';
import {CHART_COLORS_DISCRETE_PALETTE, DEFAULT_CHART_MARGIN} from './internals/utils/Theme';
import {VisxAxisScale, VisxScaleType} from './internals/VisxUtilityTypes';
import {
  BenchlingChartPropWrapper,
  ContinuousAxisConfig,
  DiscreteAxisConfig,
  ErrorBarConfig,
  LabelCompatibleType,
  LegendConfig,
} from './Types';
export {BarChartType} from './internals/bar-chart/BarChartTypes';

const DEFAULT_BAR_CHART_MARGINS = {
  top: DEFAULT_CHART_MARGIN,
  bottom: DEFAULT_CHART_MARGIN,
  left: DEFAULT_CHART_MARGIN,
  right: DEFAULT_CHART_MARGIN,
};

export const BAR_TO_TOOLTIP_PADDING = 10;
export const NO_LABEL_SENTINEL = '__points';

export const BAR_CHART_TYPE_VALIDATION_MAP: ColumnValidationMap<BarChartColumns> = {
  x: {
    allowedLeafTypes: [...NUMERICAL_LEAF_TYPES, LeafType.TEXT, LeafType.DATE, LeafType.DATETIME],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.ALLOWED,
  },
  y: {
    allowedLeafTypes: NUMERICAL_LEAF_TYPES,
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  series: {
    allowedLeafTypes: [...NUMERICAL_LEAF_TYPES, LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.ALLOWED,
  },
  errorValue: {
    allowedLeafTypes: NUMERICAL_LEAF_TYPES,
    nullableType: ColumnTypeValidationFlag.ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
};

type BarChartRequiredColumns = 'x' | 'y';
type BarChartOptionalColumns = 'series' | 'errorValue';

type BarChartColumns = BarChartRequiredColumns | BarChartOptionalColumns;

export type BarChartColumnsMapping = ColumnsMapping<BarChartRequiredColumns, BarChartOptionalColumns>;

export interface BarChartConfig {
  errorBarConfig?: ErrorBarConfig;
  legend?: LegendConfig;
  type: BarChartType;
  xAxis: DiscreteAxisConfig;
  yAxis: ContinuousAxisConfig;
}

export interface BarChartPoint {
  x: string;
  y: number;
  series: string;
  errorValue?: number;
}

export interface BarChartProps {
  columnsMapping: BarChartColumnsMapping;
  config: BarChartConfig;
  dataFrame: DataFrame;
}

export function getBarChartAccessors(
  dataFrame: DataFrame,
  columnsMapping: BarChartColumnsMapping
): BarChartAccessors {
  const {
    accessors: {rowId: rowIdAccessor, ...accessors},
    validatedLeafTypes,
  } = createAccessors(dataFrame, columnsMapping, BAR_CHART_TYPE_VALIDATION_MAP);

  const xAccessor = resolvedStringAccessor(
    accessors.x,
    dataFrame.hydratedProperties,
    VISUALIZATION_RESOLUTION_CONFIG
  );
  const yAccessor = numericAccessor(accessors.y, validatedLeafTypes.y);

  const seriesAccessor =
    accessors.series == null
      ? (): string => NO_LABEL_SENTINEL
      : resolvedStringAccessor(
          accessors.series,
          dataFrame.hydratedProperties,
          VISUALIZATION_RESOLUTION_CONFIG
        );

  return {
    xAccessor,
    yAccessor,
    rowIdAccessor,
    seriesAccessor,
    ...(accessors.errorValue != null
      ? {
          errorValueAccessor: numericAccessor(accessors.errorValue, validatedLeafTypes.y),
        }
      : {}),
  };
}

export function getBarChartLegendOptions(
  seriesUniqueValues: Array<string>,
  {legend}: BarChartConfig,
  width: number,
  height: number
): {
  colorConfig: DiscreteSeriesColorConfig;
  colorScale: BarChartScales['colorScale'];
  legendAdjustedHeight: number;
  legendHeight: number;
  showLegend: boolean;
} {
  const colorScale = scaleOrdinal({
    domain: seriesUniqueValues,
    range: CHART_COLORS_DISCRETE_PALETTE,
  });

  const legendHeight = estimateDiscreteSeriesLegendHeight(colorScale, width);
  const showLegend = legend != null;

  const legendAdjustedHeight = height - (showLegend ? legendHeight : 0);

  return {
    colorConfig: {
      colorPalette: ColorPalette.DISCRETE,
      colorScale,
    },
    colorScale,
    legendAdjustedHeight,
    legendHeight,
    showLegend,
  };
}

/**
 * Covert a DataFrame to an intermediate shape needed to call `convertDataFrameToBarGroupDicts` where we
 * have data frame rows partitioned into bar groups and each series value in that group is mapped to the row
 * that produced it.
 *
 * We return both the name of the group and the rows in that group.
 *
 *
 * Having this data structure built separate from `convertDataFrameToBarGroupDicts` allows us to retrieve the
 * original rows that created a certain bar.
 *
 * Example:
 *   This data frame:
 *
 *   ```
 *   {
 *      [...],
 *      rows: [
 *          {x: "2020", y: 2, series: "a"},
 *          {x: "2021", y: 3, series: "a"},
 *          {x: "2020", y: 1, series: "b"},
 *          {x: "2021", y: 5, series: "b"}
 *      ]
 *   }
 *   ```
 *
 *  Becomes:
 *  ```
 *  [
 *    {
 *      groupName: "2020",
 *      rowMaps: {
 *        a: {x: "2020", y: 2, series: "a"},
 *        b: {x: "2020", y: 1, series: "b"}
 *      }
 *    },
 *    {
 *      groupName: "2021",
 *      rowMaps: {
 *        a: {x: "2021", y: 3, series: "a"},
 *        b: {x: "2021", y: 5, series: "b"}
 *      }
 *    },
 *  ]
 *  ```
 */
export function convertDataFrameToBarGroupRowMaps(
  dataFrame: DataFrame,
  xAccessor: DataFrameValueAccessor<string>,
  seriesAccessor: DataFrameValueAccessor<string>
): BarGroupRowMaps {
  return Object.entries(groupBy<DataFrameRow, string>(dataFrame.rows, xAccessor)).map(([xValue, rows]) => ({
    groupName: xValue,
    rowMap: objectFromEntries(rows.map((row) => [seriesAccessor(row), row])),
  }));
}

/**
 * Convert a `barGroupRowMaps` generated by `convertDataFrameToBarGroupRowMaps` to a shape that is compatible
 * with `BarGroup`'s expected data
 *
 * `BarGroup` accepts data that is an array of bar groups where we have a field for the x-axis value and a field
 * for each of the series that have a value in on that x-axis group. We also add a map of the x-axis value to
 * to the original row that.
 *
 * Example:
 *   This data frame:
 *  ```
 *  [
 *    {
 *      groupName: "2020",
 *      rowMaps: {
 *        a: {x: "2020", y: 2, series: "a"},
 *        b: {x: "2020", y: 1, series: "b"}
 *      }
 *    },
 *    {
 *      groupName: "2021",
 *      rowMaps: {
 *        a: {x: "2021", y: 3, series: "a"},
 *        b: {x: "2021", y: 5, series: "b"}
 *      }
 *    },
 *  ]
 *  ```
 *
 *  Becomes:
 *  ```
 *  [
 *    {
 *       x: "2020",
 *       a: 2,
 *       b: 1
 *    },
 *    {
 *       x: "2021",
 *       a: 3,
 *       b: 5
 *    }
 *  ]
 *  ```
 */
export function convertDataFrameToBarGroupDicts(
  barGroupRowMaps: BarGroupRowMaps,
  yAccessor: DataFrameValueAccessor<number>,
  columnsMapping: BarChartColumnsMapping
): BarGroupDicts {
  return barGroupRowMaps.map(({groupName, rowMap}) => ({
    [columnsMapping.x]: groupName,
    ...objectFromEntries(Object.entries(rowMap).map(([barKey, row]) => [barKey, yAccessor(row)])),
  }));
}

export function getBarChartYAxisExtent(
  type: BarChartType,
  barGroupRowMaps: BarGroupRowMaps,
  yAccessor: BarChartAccessors['yAccessor'],
  yValues: Array<number>,
  errorValues: Array<number> | null
): [number, number] {
  switch (type) {
    case BarChartType.GROUPED:
      return getGroupedBarChartYExtent(yValues, errorValues);
    case BarChartType.STACKED:
      return getStackedBarChartYExtent(barGroupRowMaps, yAccessor, yValues);
    default:
      assertAndContinue(false, `Invalid bar chart type: ${assertNever(type)}`);
      return [0, 0];
  }
}

export function getBarChartAxisConfigs(
  {xAxis, yAxis}: BarChartConfig,
  xUniqueValues: Array<string>,
  yExtent: [number, number],
  chartWidth: number
): {
  xAxisConfig: BandVisxAxisConfig;
  yAxisConfig: LinearVisxAxisConfig | LogVisxAxisConfig;
} {
  const xAxisNumTicks = getDiscreteAxisNumTicks(xUniqueValues.length, chartWidth);
  const xAxisConfig = getVisxAxisConfig(xAxis, DataType.STRING, xUniqueValues, {
    suggestedNumTicks: xAxisNumTicks,
  });

  const yAxisConfig = getVisxAxisConfig(yAxis, DataType.NUMBER, yExtent, {
    extentOverride: [yAxis.min ?? null, yAxis.max ?? null],
  });

  assertVisxAxisConfigScaleType(xAxisConfig, VisxScaleType.BAND);
  assert(
    isVisxAxisConfigScaleType(yAxisConfig, VisxScaleType.LINEAR) ||
      isVisxAxisConfigScaleType(yAxisConfig, VisxScaleType.LOG),
    'Expected axis scale to be either LINEAR or LOG'
  );

  return {
    xAxisConfig,
    yAxisConfig,
  };
}

export function getBarChartScales(
  xAxisConfig: BandVisxAxisConfig,
  yAxisConfig: LinearVisxAxisConfig | LogVisxAxisConfig,
  chartWidth: number,
  chartHeight: number
): Omit<BarChartScales, 'colorScale'> {
  const xScale = xAxisConfig.scale
    .range([0, chartWidth])
    .paddingInner(BAR_PADDING_BETWEEN_GROUPS)
    .paddingOuter(BAR_PADDING_BETWEEN_GROUPS / 2);

  const yScale = yAxisConfig.scale.range([chartHeight, 0]) as
    | VisxAxisScale<VisxScaleType.LINEAR>
    | VisxAxisScale<VisxScaleType.LOG>;

  return {xScale, yScale};
}

export function getBarChartUseChartTooltipInput(
  columnsMapping: BarChartColumnsMapping,
  config: BarChartConfig
): UseChartTooltipInput<DataFrameRow> {
  const baseHookInput = {
    axisTypes: {
      [columnsMapping.x]: DataType.STRING,
      [columnsMapping.y]: DataType.NUMBER,
    },
    columnLabels: {
      [columnsMapping.x]: config.xAxis.label,
      [columnsMapping.y]: config.yAxis.label,
    },
    columnNames: [columnsMapping.x, columnsMapping.y],
  };

  const errorValueColumnMapping = columnsMapping.errorValue;

  return errorValueColumnMapping == null
    ? baseHookInput
    : produce(baseHookInput, (draftHookInput) => {
        draftHookInput.axisTypes[errorValueColumnMapping] = DataType.NUMBER;
        draftHookInput.columnLabels[errorValueColumnMapping] = config.errorBarConfig!.errorValueLabel;
        draftHookInput.columnNames.push(errorValueColumnMapping);
      });
}

export function getBarChartTooltipInfo(
  {
    barProperties: {color, x: barX, y: barY, width: barWidth},
    barGroupInfo: {x: barGroupX},
    dataFrameRow,
  }: BarChartHoverInfo,
  seriesAccessor: DataFrameValueAccessor<string>,
  isSeriesEnabled: boolean
): TooltipInfo<DataFrameRow> {
  const offsetTop = -1 * (estimateTooltipHeight(2, isSeriesEnabled) + BAR_TO_TOOLTIP_PADDING);

  return {
    point: dataFrameRow,
    // if the yAxis range is less than the value of the bar, then barY will be negative, and may push the
    // tooltip out of the chart area, so we use the height of the tooltip as the minimum 'top' value.
    top: Math.max(offsetTop, barY + offsetTop),
    left: barGroupX + barX + barWidth / 2,
    ...(isSeriesEnabled && {
      label: {
        text: seriesAccessor(dataFrameRow),
        color,
      },
    }),
  };
}

export function validateBarChartInput(
  columnsMapping: BarChartColumnsMapping,
  {errorBarConfig, legend, yAxis: {min, max}}: BarChartConfig,
  isErrorBarsEnabled: boolean,
  isSeriesEnabled: boolean
): void {
  assertAndContinue(
    legend == null || (legend != null && isSeriesEnabled),
    'The series columnsMapping needs to be specified to enable legends.',
    {columnsMapping, legend}
  );

  assertAndContinue(
    errorBarConfig == null || (errorBarConfig != null && isErrorBarsEnabled),
    'The errorValue columnsMapping needs to be specified to enable error bars.',
    {columnsMapping, errorBarConfig}
  );
}
/**
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export function BarChart({
  clientId,
  columnsMapping,
  config,
  dataFrame,
  height,
  width,
}: BenchlingChartPropWrapper<BarChartProps>): JSX.Element {
  const isErrorBarsEnabled = columnsMapping.errorValue != null;
  const isSeriesEnabled = columnsMapping.series != null;
  // TODO(BNCH-37582): Revisit if we need to put this code in useEffect/userMemo hooks for performance concerns
  validateBarChartInput(columnsMapping, config, isErrorBarsEnabled, isSeriesEnabled);

  const accessors = getBarChartAccessors(dataFrame, columnsMapping);
  const {xAccessor, yAccessor, seriesAccessor, errorValueAccessor} = accessors;

  const {excludedSeries, filteredDataFrame, toggleExcludedSeries} = useSeriesExcludedWithDataFrameState(
    dataFrame,
    seriesAccessor
  );

  const xUniqueValues = _.unique(dataFrame.rows.map((row) => xAccessor(row)));
  const seriesUniqueValues = _.unique(dataFrame.rows.map((row) => seriesAccessor(row)));

  const filteredYValues = filteredDataFrame.rows.map((row) => yAccessor(row));
  const filteredErrorValues =
    errorValueAccessor == null ? null : filteredDataFrame.rows.map((row) => errorValueAccessor(row));

  const {colorConfig, colorScale, legendAdjustedHeight, legendHeight, showLegend} = getBarChartLegendOptions(
    seriesUniqueValues,
    config,
    width,
    height
  );

  const {chartWidth: initialChartWidth} = getChartDimensions(
    DEFAULT_BAR_CHART_MARGINS,
    width,
    legendAdjustedHeight
  );

  const barGroupRowMaps = convertDataFrameToBarGroupRowMaps(filteredDataFrame, xAccessor, seriesAccessor);
  const barGroupDicts = convertDataFrameToBarGroupDicts(barGroupRowMaps, yAccessor, columnsMapping);

  const yExtent = getBarChartYAxisExtent(
    config.type,
    barGroupRowMaps,
    yAccessor,
    filteredYValues,
    filteredErrorValues
  );
  const initialAxisConfigs = getBarChartAxisConfigs(config, xUniqueValues, yExtent, initialChartWidth);
  const {chartWidth, chartHeight, margins, xAxisConfig, yAxisConfig} = fitChartAndLabels({
    defaultMargins: DEFAULT_BAR_CHART_MARGINS,
    height: legendAdjustedHeight,
    width,
    xAxisOrientation: AxisOrientation.BOTTOM,
    ...initialAxisConfigs,
  });

  const axisScales = getBarChartScales(xAxisConfig, yAxisConfig, chartWidth, chartHeight);

  const useChartTooltipInput = getBarChartUseChartTooltipInput(columnsMapping, config);
  const {tooltipProps, updateTooltipProps} = useChartTooltip<DataFrameRow>(useChartTooltipInput);
  const [hoveredRow, setHoveredRow] = useState<DataFrameRow | null>(null);
  const [highlightedSeries, setHighlightedSeries] = useState<LabelCompatibleType | null>(null);

  function handleOnHoverBar(hoverInfo: BarChartHoverInfo | null): void {
    if (hoverInfo != null) {
      setHoveredRow(hoverInfo.dataFrameRow);
      setHighlightedSeries(seriesAccessor(hoverInfo.dataFrameRow));
      updateTooltipProps(getBarChartTooltipInfo(hoverInfo, seriesAccessor, isSeriesEnabled));
    } else {
      setHoveredRow(null);
      setHighlightedSeries(null);
      updateTooltipProps(null);
    }
  }

  function handleOnHoverLegendItem(label?: LabelCompatibleType): void {
    setHighlightedSeries(label ?? null);
  }

  const barChartContentProps = {
    accessors,
    barGroupDicts,
    barGroupRowMaps,
    chartWidth,
    chartHeight,
    handleOnHoverBar,
    hoveredRow,
    highlightedSeries,
    isSeriesEnabled,
    seriesUniqueValues,
    scales: {
      ...axisScales,
      colorScale,
    },
  };

  const BarChartContentComponent = BAR_CHART_TYPE_TO_CONTENT_COMPONENT[config.type];

  return (
    <>
      {showLegend && (
        <DiscreteSeriesLegend
          colorConfig={colorConfig}
          excludedSeries={excludedSeries}
          onClickLegendItem={toggleExcludedSeries}
          onHoverItem={handleOnHoverLegendItem}
        />
      )}
      <svg height={height - (showLegend ? legendHeight : 0)} width={width}>
        {tooltipProps && <ChartTooltip {...tooltipProps} />}
        <Group left={margins.left} top={margins.top}>
          <ChartMask width={chartWidth} height={chartHeight} />
          <Group mask={`url(#${getChartMaskId(clientId)})`}>
            <ChartGrid
              xAxisConfig={xAxisConfig}
              yAxisConfig={yAxisConfig}
              width={chartWidth}
              height={chartHeight}
            />
            <BarChartContentComponent {...barChartContentProps} />
          </Group>
          <AxisLeft
            axisConfig={yAxisConfig}
            longestYTickLabelWidth={Math.max(...getTickLabelWidths(yAxisConfig))}
          />
          <AxisBottom axisConfig={xAxisConfig} chartHeight={chartHeight} marginBottom={margins.bottom} />
        </Group>
      </svg>
    </>
  );
}

/**
 * @deprecated Charts should be created through backend Chart model and rendered through /charts/chart-content module. Reach out
 * to Insights team for more details on charting.
 */
export default function BarChartWrapper(props: BarChartProps): JSX.Element {
  return (
    <BenchlingChart dataTestAttrs={{component: 'BarChart', element: 'root'}}>
      {(benchlingChartProps) => <BarChart {...props} {...benchlingChartProps} />}
    </BenchlingChart>
  );
}
